'use strict';

const $ = require('jquery');
const Backbone = require('backbone');
const Actions = require('common/domain/action/index');
const Dispatcher = require('common/domain/repository/dispatcher');
const Cacher = require('common/util/cacher');
const DiagramAPI = require('system/infra/api/diagram');

const DiagramActionCreator = Backbone.Model.extend({
    initialize: function() {
        this._fetched = {};
        this._fetchedDirectories = {};
    },

    _contextToKey: function(key, dt) {
        return `${key}:${dt}`;
    },

    fetchDiagram: function(projectID, diagramID, dt) {
        return new Promise((resolve, reject) => {
            DiagramAPI.getDiagram(projectID, diagramID, dt)
                .then(diagram => {
                    Dispatcher.dispatch({
                        type: Actions.CREATE_DIAGRAM,
                        context: {
                            projectId: projectID,
                            dt: dt
                        },
                        diagram: diagram,
                        options: {fetched: true}
                    });
                    resolve();
                }, reject);
        });
    },

    fetchDiagramOnce: function(projectID, diagramID, dt) {
        const key = this._contextToKey(`${projectID}:${diagramID}`, dt);
        let promise = this._fetched[key];
        if(!promise) {
            promise = this.fetchDiagram(projectID, diagramID, dt);
            this._fetched[key] = promise;
        }
        return promise;
    },

    fetchDiagrams: function(projectId, dt) {
        return new Promise((resolve, reject) => {
            DiagramAPI.getDiagramsNotInDirectory(projectId, dt)
                .then(diagrams => {
                    Dispatcher.dispatch({
                        type: Actions.CREATE_DIAGRAM,
                        context: {
                            projectId: projectId,
                            dt: dt
                        },
                        diagram: diagrams,
                        options: {fetched: true}
                    });
                    resolve();
                }, reject);
        });
    },

    fetchDiagramsOnce: function(projectId, dt) {
        const key = this._contextToKey(projectId, dt);
        let promise = this._fetched[key];
        if(!promise) {
            promise = this.fetchDiagrams(projectId, dt);
            this._fetched[key] = promise;
        }
        return promise;
    },

    fetchDiagramsInDirectory: function(projectId, directoryId, dt) {
        return new Promise((resolve, reject) => {
            DiagramAPI.getDiagramsInDirectory(projectId, directoryId, dt)
                .then(diagrams => {
                    Dispatcher.dispatch({
                        type: Actions.CREATE_DIAGRAM,
                        context: {
                            projectId: projectId,
                            dt: dt
                        },
                        diagram: diagrams,
                        options: {fetched: true}
                    });
                    resolve();
                }, reject);
        });
    },

    fetchDiagramsInDirectoryOnce: function(projectId, directoryId, dt) {
        const key = this._contextToKey(`${projectId}:${directoryId}`, dt);
        let promise = this._fetched[key];
        if(!promise) {
            promise = this.fetchDiagramsInDirectory(projectId, directoryId, dt);
            this._fetched[key] = promise;
        }
        return promise;
    },

    fetchDirectories: function(projectId, dt) {
        return new Promise((resolve, reject) => {
            DiagramAPI.getDiagramDirectories(projectId, dt)
                .then(directories => {
                    Dispatcher.dispatch({
                        type: Actions.CREATE_DIAGRAM_DIRECTORY,
                        context: {
                            projectId: projectId
                        },
                        directory: directories,
                        options: {fetched: true}
                    });
                    resolve();
                }, reject);
        });
    },

    fetchDirectoriesOnce: Cacher(function() {
        return {
            store: this._fetchedDirectories,
            func: this.fetchDirectories
        }
    }),

    mergeDiagramMetas: function(projectId, diagramId) {
        return new Promise((resolve, reject) => {
            DiagramAPI.getDiagram(projectId, diagramId)
                .then(diagram => {
                    const context = {
                        projectId: projectId
                    };
                    Dispatcher.dispatch({
                        type: Actions.MERGE_NODE_META,
                        context: context,
                        nodeMeta: _.map(diagram.records, function(j, uid) {
                            j.id = uid;
                            return j;
                        })
                    });
                    Dispatcher.dispatch({
                        type: Actions.MERGE_LINK_META,
                        context: context,
                        linkMeta: _.map(diagram.relations, function(j, uid) {
                            j.id = uid;
                            return j;
                        })
                    });
                    resolve();
                }, reject);
        });
    },

    createDiagram: function(projectId, diagramJson) {
        return new Promise((resolve, reject) => {
            DiagramAPI.createDiagram(projectId, diagramJson)
                .then(diagram => {
                    Dispatcher.dispatch({
                        type: Actions.CREATE_DIAGRAM,
                        context: {
                            projectId: projectId
                        },
                        diagram: diagram
                    });
                    resolve();
                }, reject);
        });
    },

    updateDiagram: function(projectId, diagramId, diagramJson) {
        return new Promise((resolve, reject) => {
            DiagramAPI.updateDiagram(projectId, diagramId, diagramJson)
                .then(diagram => {
                    Dispatcher.dispatch({
                        type: Actions.UPDATE_DIAGRAM,
                        context: {
                            projectId: projectId
                        },
                        diagram: diagram
                    });
                    resolve();
                }, reject);
        });
    },

    deleteDiagram: function(projectId, diagramId) {
        return new Promise((resolve, reject) => {
            DiagramAPI.deleteDiagram(projectId, diagramId)
                .then(diagram => {
                    Dispatcher.dispatch({
                        type: Actions.DELETE_DIAGRAM,
                        context: {
                            projectId: projectId
                        },
                        diagram: {
                            id: diagramId
                        }
                    });
                    resolve();
                }, reject);
        });
    },

    createDirectory: function(projectId, directoryJson) {
        return new Promise((resolve, reject) => {
            DiagramAPI.createDiagramDirectory(projectId, directoryJson)
                .then(directory => {
                    Dispatcher.dispatch({
                        type: Actions.CREATE_DIAGRAM_DIRECTORY,
                        context: {
                            projectId: projectId
                        },
                        directory: directory
                    });
                    resolve();
                }, reject);
        });
    },

    updateDirectory: function(projectId, directoryId, directoryJson) {
        return new Promise((resolve, reject) => {
            DiagramAPI.updateDiagramDirectory(
                projectId, directoryId, directoryJson)
                .then(directory => {
                    Dispatcher.dispatch({
                        type: Actions.UPDATE_DIAGRAM_DIRECTORY,
                        context: {
                            projectId: projectId
                        },
                        directory: directory
                    });
                    resolve();
                }, reject);
        });
    },

    deleteDirectory: function(projectId, directoryId) {
        return new Promise((resolve, reject) => {
            DiagramAPI.deleteDiagramDirectory(projectId, directoryId)
                .then(directory => {
                    Dispatcher.dispatch({
                        type: Actions.DELETE_DIAGRAM_DIRECTORY,
                        context: {
                            projectId: projectId
                        },
                        directory: directory
                    });
                    resolve();
                }, reject);
        });
    }
});

module.exports = new DiagramActionCreator();



// WEBPACK FOOTER //
// ./system/domain/action/diagram.js